import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useBudget = () => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [budgetDetails, setBudgetDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeBudgetForDept, setActiveBudgetForDept] = useState(null);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const activeBudgetRequestRef = useRef(0);

  // Fetch all departments
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.finance.departments,
        null,
        navigate,
      );
      if (response.ok) {
        setDepartments(
          Array.isArray(response.data)
            ? response.data
            : response.data?.results || [],
        );
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, [navigate]);

  // Fetch all subjects (for attaching to departments)
  const fetchSubjects = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.subject,
        null,
        navigate,
      );
      if (response.ok) {
        setSubjects(
          Array.isArray(response.data)
            ? response.data
            : response.data?.results || [],
        );
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  }, [navigate]);
  const createDepartment = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.departments,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Item created successfully");
          fetchDepartments();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create item");
          return null;
        }
      } catch (error) {
        message.error("Error creating item");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchDepartments],
  );

  // Update a department
  const updateDepartment = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.departmentDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Item updated successfully");
          fetchDepartments();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update item");
          return null;
        }
      } catch (error) {
        message.error("Error updating item");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchDepartments],
  );

  // Delete a department
  const deleteDepartment = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.departmentDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Item deleted successfully");
          fetchDepartments();
          return true;
        } else {
          Utilities.showError(response, "Failed to delete item");
          return false;
        }
      } catch (error) {
        message.error("Error deleting item");
        console.error(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchDepartments],
  );

  // Fetch all budgets with optional filters
  const fetchBudgets = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.departmentId) queryObj.department_id = params.departmentId;
        if (params.fiscalYear) queryObj.fiscal_year = params.fiscalYear;
        if (params.term) queryObj.term = params.term;
        if (params.status) queryObj.status = params.status;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.budgets}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setBudgets(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(response, "Failed to fetch budgets");
        }
      } catch (error) {
        message.error("Error fetching budgets");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Fetch budget details by ID
  const fetchBudgetDetails = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetDetail.replace(":id", id);
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setBudgetDetails(response.data);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch budget details");
          return null;
        }
      } catch (error) {
        message.error("Error fetching budget details");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Create a new budget
  const createBudget = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.budgets,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Budget created successfully");
          fetchBudgets();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create budget");
          return null;
        }
      } catch (error) {
        message.error("Error creating budget");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgets],
  );

  // Update a budget
  const updateBudget = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetDetail.replace(":id", id);
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Budget updated successfully");
          fetchBudgets();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update budget");
          return null;
        }
      } catch (error) {
        message.error("Error updating budget");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgets],
  );

  // Submit budget for approval
  const submitBudget = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetSubmit.replace(":id", id);
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );

        if (response.ok) {
          message.success("Budget submitted for approval");
          fetchBudgets();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to submit budget");
          return null;
        }
      } catch (error) {
        message.error("Error submitting budget");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgets],
  );

  // Approve or reject budget using the same review endpoint
  const approveBudget = useCallback(
    async (id, action = "approve", remarks = "") => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetApprove.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { action, remarks },
          navigate,
        );

        if (response.ok) {
          const successMessage =
            action === "reject"
              ? "Budget rejected and returned to draft"
              : "Budget approved and activated successfully";
          message.success(successMessage);
          fetchBudgets();
          if (budgetDetails?.budgetId === id) {
            setBudgetDetails(response.data?.budget || null);
          }
          return response.data;
        } else {
          const errorMessage =
            action === "reject"
              ? "Failed to reject budget"
              : "Failed to approve budget";
          Utilities.showError(response, errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          action === "reject"
            ? "Error rejecting budget"
            : "Error approving budget";
        message.error(errorMessage);
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgets, budgetDetails],
  );

  // Activate budget
  const activateBudget = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetActivate.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );

        if (response.ok) {
          message.success("Budget activated successfully");
          fetchBudgets();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to activate budget");
          return null;
        }
      } catch (error) {
        message.error("Error activating budget");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgets],
  );

  // Add line items to budget
  const addLineItems = useCallback(
    async (id, lineItems) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetAddLineItems.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          lineItems,
          navigate,
        );

        if (response.ok) {
          message.success("Line items added successfully");
          fetchBudgetDetails(id);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to add line items");
          return null;
        }
      } catch (error) {
        message.error("Error adding line items");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgetDetails],
  );

  // Update a line item
  const updateLineItem = useCallback(
    async (budgetId, lineItemId, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetUpdateLineItem.replace(
          ":id",
          budgetId,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          { lineItemId, ...data },
          navigate,
        );

        if (response.ok) {
          message.success("Line item updated successfully");
          fetchBudgetDetails(budgetId);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update line item");
          return null;
        }
      } catch (error) {
        message.error("Error updating line item");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgetDetails],
  );

  // Delete a line item
  const deleteLineItem = useCallback(
    async (budgetId, lineItemId) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.budgetDeleteLineItem.replace(
          ":id",
          budgetId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { lineItemId },
          navigate,
        );

        if (response.ok) {
          message.success("Line item deleted successfully");
          fetchBudgetDetails(budgetId);
          return true;
        } else {
          Utilities.showError(response, "Failed to delete line item");
          return false;
        }
      } catch (error) {
        message.error("Error deleting line item");
        console.error(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchBudgetDetails],
  );

  // Fetch the active budget for a specific department (reuses the budgets list endpoint)
  const fetchActiveBudgetForDepartment = useCallback(
    async (departmentId) => {
      const requestId = ++activeBudgetRequestRef.current;
      if (!departmentId) {
        setActiveBudgetForDept(null);
        return null;
      }
      setLineItemsLoading(true);
      try {
        const url = `${domainSettings.urls.finance.budgets}?department_id=${departmentId}&status=Active`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const results = Array.isArray(response.data)
            ? response.data
            : response.data?.results || [];
          if (requestId !== activeBudgetRequestRef.current) return null;
          if (results.length === 0) {
            setActiveBudgetForDept(null);
            return null;
          }
          const budget = results[0];
          // Keep all line items; the modal filters out exhausted ones in the selector
          setActiveBudgetForDept(budget);
          return budget;
        } else {
          setActiveBudgetForDept(null);
          return null;
        }
      } catch (error) {
        console.error("Error fetching active budget for department:", error);
        if (requestId === activeBudgetRequestRef.current) {
          setActiveBudgetForDept(null);
        }
        return null;
      } finally {
        if (requestId === activeBudgetRequestRef.current) {
          setLineItemsLoading(false);
        }
      }
    },
    [navigate],
  );

  // Delete budget
  const deleteBudget = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.finance.budgetDetail.replace(":id", id);
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Budget deleted successfully");
          fetchBudgets();
          return true;
        } else {
          Utilities.showError(response, "Failed to delete budget");
          return false;
        }
      } catch (error) {
        message.error("Error deleting budget");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchBudgets],
  );

  // Get budget statistics
  const getStatistics = useCallback(() => {
    return {
      total: budgets.length,
      draft: budgets.filter((b) => b.status === "Draft").length,
      submitted: budgets.filter((b) => b.status === "Submitted").length,
      approved: budgets.filter((b) => b.status === "Approved").length,
      active: budgets.filter((b) => b.status === "Active").length,
      totalAllocated: budgets.reduce(
        (sum, b) => sum + parseFloat(b.totalAmount || 0),
        0,
      ),
      totalSpent: budgets.reduce(
        (sum, b) => sum + parseFloat(b.amountSpent || 0),
        0,
      ),
      totalCommitted: budgets.reduce(
        (sum, b) => sum + parseFloat(b.amountCommitted || 0),
        0,
      ),
    };
  }, [budgets]);

  return {
    departments,
    subjects,
    fetchSubjects,
    budgets,
    budgetDetails,
    loading,
    activeBudgetForDept,
    lineItemsLoading,
    fetchDepartments,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    fetchBudgets,
    fetchBudgetDetails,
    createBudget,
    updateBudget,
    submitBudget,
    approveBudget,
    activateBudget,
    addLineItems,
    updateLineItem,
    deleteLineItem,
    deleteBudget,
    fetchActiveBudgetForDepartment,
    getStatistics,
  };
};

export default useBudget;
